import { SUPPLIERS } from './consts';

export const calculateWFSFee = (dimensions, weight, options = {}) => {
  try {
    if (!dimensions || !weight) return 0.00;

    const { length, width, height } = dimensions;
    const {
      isApparel = false,
      isHazardous = false,
      retailPrice = null,
    } = options;

    // Validate we have all required data
    if (!length || !width || !height || length <= 0 || width <= 0 || height <= 0 || weight <= 0) {
      return 0.00;
    }

    // Sort dimensions to identify longest, median, and shortest sides
    const sortedDimensions = [length, width, height].sort((a, b) => b - a);
    const [longestSide, medianSide, shortestSide] = sortedDimensions;

    // Calculate girth using the two shorter dimensions
    const girth = 2 * (medianSide + shortestSide);
    const longestPlusGirth = longestSide + girth;

    // Check if item meets standard fulfillment criteria
    if (weight > 150 || longestSide > 108 || longestPlusGirth > 165) {
      return 0.00;
    }

    // Determine if item is oversize and calculate base shipping weight
    let isOversize = false;
    let oversizeFee = 0;
    let baseShippingWeight = weight;

    // Check oversize criteria
    if ((longestSide > 48 && longestSide <= 96) ||
      medianSide > 30 ||
      (longestPlusGirth > 105 && longestPlusGirth <= 130)) {
      isOversize = true;
      oversizeFee = 3.00;
    } else if ((longestSide > 96 && longestSide <= 108) ||
      (longestPlusGirth > 130 && longestPlusGirth <= 165)) {
      isOversize = true;
      oversizeFee = 20.00;
      baseShippingWeight = Math.max(weight, 90);
    }

    let shippingWeight;

    // For items less than 1 lb, use unit weight only
    if (weight < 1) {
      shippingWeight = Math.ceil(weight + 0.25);
    } else {
      const dimWeight = (length * width * height) / 139;
      const greaterWeight = Math.max(baseShippingWeight, dimWeight);
      shippingWeight = Math.ceil(greaterWeight + 0.25);
    }

    // Calculate base fulfillment fee
    let baseFee = 0;

    if (shippingWeight <= 1) {
      baseFee = 3.45;
    } else if (shippingWeight === 2) {
      baseFee = 4.95;
    } else if (shippingWeight === 3) {
      baseFee = 5.45;
    } else if (shippingWeight >= 4 && shippingWeight <= 20) {
      const additionalWeight = shippingWeight - 4;
      baseFee = 5.75 + (0.40 * additionalWeight);
    } else if (shippingWeight >= 21 && shippingWeight <= 30) {
      const additionalWeight = shippingWeight - 21;
      baseFee = 15.55 + (0.40 * additionalWeight);
    } else if (shippingWeight >= 31 && shippingWeight <= 50) {
      const additionalWeight = shippingWeight - 31;
      baseFee = 14.55 + (0.40 * additionalWeight);
    } else if (shippingWeight >= 51) {
      const additionalWeight = shippingWeight - 51;
      baseFee = 17.55 + (0.40 * additionalWeight);
    }

    // Calculate additional fees
    let additionalFees = 0;

    if (isApparel) {
      additionalFees += 0.50;
    }

    if (isHazardous) {
      additionalFees += 0.50;
    }

    if (retailPrice !== null && retailPrice < 10) {
      additionalFees += 1.00;
    }

    additionalFees += oversizeFee;

    const totalFee = baseFee + additionalFees;

    return Math.round(totalFee * 100) / 100;

  } catch (error) {
    console.error('Error calculating WFS fee:', error);
    return 0.00;
  }
};

export const calculateReferralFee = (salePrice, category, subCategory = '') => {
  try {
    const price = parseFloat(salePrice);
    if (!price || price <= 0) return 0.00;

    const cat = category?.toLowerCase() || '';
    const subCat = subCategory?.toLowerCase() || '';

    // Apparel & Accessories
    if (cat.includes('apparel') || cat.includes('clothing') || cat.includes('accessories')) {
      if (price <= 15) return price * 0.05;
      if (price <= 20) return price * 0.10;
      return price * 0.15;
    }

    // Appliances - Compact
    if ((cat.includes('appliances') && cat.includes('compact')) ||
      subCat.includes('compact appliance')) {
      if (price <= 300) return price * 0.12;
      return 300 * 0.12 + (price - 300) * 0.08;
    }

    // Appliances - Major
    if (cat.includes('appliances') && cat.includes('major')) {
      return price * 0.08;
    }

    // Automotive & Powersports
    if (cat.includes('automotive') || cat.includes('powersports') ||
      cat.includes('auto')) {
      if (subCat.includes('tires') || subCat.includes('wheels')) {
        return price * 0.10;
      }
      return price * 0.12;
    }

    // Baby Products
    if (cat.includes('baby')) {
      if (price <= 10) return price * 0.08;
      return price * 0.15;
    }

    // Base Power Tools
    if (subCat.includes('base power tools')) {
      return price * 0.12;
    }

    // Beauty, Health and Personal Care
    if (cat.includes('beauty') || cat.includes('health') ||
      cat.includes('personal care') || cat.includes('care')) {
      if (price <= 10) return price * 0.08;
      return price * 0.15;
    }

    // Books
    if (cat.includes('books') || cat.includes('book')) {
      return price * 0.15;
    }

    // Camera & Photo
    if (cat.includes('camera') || cat.includes('photo') ||
      subCat.includes('camera')) {
      return price * 0.08;
    }

    // Collectibles
    if (cat.includes('collectibles') || cat.includes('collectible')) {
      return price * 0.08;
    }

    // Consumer Electronics
    if (cat.includes('consumer electronics') || cat.includes('electronics')) {
      return price * 0.08;
    }

    // Electronics Accessories
    if (cat.includes('electronics accessories') ||
      subCat.includes('electronics accessories')) {
      if (price <= 100) return price * 0.15;
      return 100 * 0.15 + (price - 100) * 0.08;
    }

    // Home, Kitchen, Decor & Garden
    if (cat.includes('home') || cat.includes('kitchen') ||
      cat.includes('decor') || cat.includes('garden')) {
      return price * 0.15;
    }

    // Grocery
    if (cat.includes('grocery') || cat.includes('food')) {
      if (price <= 15) return price * 0.08;
      return price * 0.15;
    }

    // Indoor & Outdoor Furniture
    if (cat.includes('furniture') || subCat.includes('furniture')) {
      if (price <= 200) return price * 0.15;
      return 200 * 0.15 + (price - 200) * 0.10;
    }

    // Industrial & Scientific Supplies
    if (cat.includes('industrial') || cat.includes('scientific')) {
      return price * 0.12;
    }

    // Jewelry and Precious Metals
    if (cat.includes('jewelry') || cat.includes('precious metals')) {
      if (price <= 250) return price * 0.20;
      return 250 * 0.20 + (price - 250) * 0.05;
    }

    // Luggage & Travel Accessories
    if (cat.includes('luggage') || cat.includes('travel')) {
      return price * 0.15;
    }

    // Music
    if (cat.includes('music') && !cat.includes('instruments')) {
      return price * 0.15;
    }

    // Musical Instruments
    if (cat.includes('musical instruments') || cat.includes('instruments')) {
      return price * 0.12;
    }

    // Office Products
    if (cat.includes('office')) {
      if (subCat.includes('printer') || subCat.includes('cartridge')) {
        return price * 0.12;
      }
      return price * 0.15;
    }

    // Outdoor Power Tools
    if (subCat.includes('outdoor power tools') ||
      (cat.includes('tools') && subCat.includes('outdoor'))) {
      if (price <= 500) return price * 0.15;
      return price * 0.08;
    }

    // Outdoors Products and Sports
    if (cat.includes('outdoors') || cat.includes('sports')) {
      if (subCat.includes('hunting trail monitors') ||
        subCat.includes('binoculars') ||
        subCat.includes('telescopes') ||
        subCat.includes('spotting scopes') ||
        subCat.includes('night vision')) {
        return price * 0.08;
      }
      return price * 0.15;
    }

    // Personal Computers
    if (cat.includes('personal computers') || cat.includes('computers') ||
      subCat.includes('computers')) {
      return price * 0.06;
    }

    // Pet Supplies
    if (cat.includes('pet')) {
      return price * 0.15;
    }

    // Plumbing, Heating, Cooling & Ventilation
    if (cat.includes('plumbing') || cat.includes('heating') ||
      cat.includes('cooling') || cat.includes('ventilation')) {
      return price * 0.10;
    }

    // Shoes, Handbags, Backpacks & Sunglasses
    if (cat.includes('shoes') || cat.includes('handbags') ||
      cat.includes('backpacks') || cat.includes('sunglasses')) {
      return price * 0.15;
    }

    // Software & Computer Video Games
    if (cat.includes('software') ||
      (cat.includes('video games') && cat.includes('computer'))) {
      return price * 0.15;
    }

    // Tires & Wheels
    if (cat.includes('tires') || cat.includes('wheels')) {
      return price * 0.10;
    }

    // Tools & Home Improvement
    if (cat.includes('tools') || cat.includes('home improvement')) {
      return price * 0.15;
    }

    // Toys & Games
    if (cat.includes('toys') || cat.includes('games')) {
      return price * 0.15;
    }

    // Video & DVD
    if (cat.includes('video') || cat.includes('dvd')) {
      return price * 0.15;
    }

    // Video Game Consoles
    if (cat.includes('video game consoles') ||
      (cat.includes('gaming') && cat.includes('console'))) {
      return price * 0.08;
    }

    // Watches
    if (cat.includes('watches') || cat.includes('watch')) {
      if (price <= 1500) return price * 0.15;
      return 1500 * 0.15 + (price - 1500) * 0.03;
    }

    // Cell Phones
    if (cat.includes('cell phones') || cat.includes('phone')) {
      return price * 0.08;
    }

    // Auto Electronics
    if (subCat.includes('auto electronics')) {
      return price * 0.08;
    }

    // Everything Else - Default 15%
    return price * 0.15;

  } catch (error) {
    console.log('Error calculating referral fee:', error);
    return 0.00;
  }
};

// Get under maintenance suppliers from environment variable
const getUnderMaintenanceSuppliers = () => {
  const envValue = process.env.REACT_APP_UNDERMAINTENANCE_SUPPLIERS || '';
  return envValue
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(s => s.length > 0);
};

const UNDERMAINTENANCE_SUPPLIERS = getUnderMaintenanceSuppliers();

// Get all supported supplier IDs from SUPPLIERS constant
const SUPPORTED_SUPPLIER_IDS = SUPPLIERS.map(s => s.id.toLowerCase());

export const getValidatedSupplierFromUrl = (url) => {
  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname.toLowerCase();

    // Remove common prefixes and extract main domain
    const domain = hostname
      .replace(/^(www\.|m\.|mobile\.)/, '')
      .split('.')[0].replace(/-/g, '');

    // Find supplier
    const supplier = SUPPLIERS.find(
      s => s.id.toLowerCase() === domain
    );

    if (!supplier) {
      const formattedDomain = domain.charAt(0).toUpperCase() + domain.slice(1);

      return {
        isValid: false,
        message: `${formattedDomain} is not yet supported for Live Scan.  
 Use the Universal Live Scan or email us at contact@multisellerpro.com to request it - we'll add it ASAP!`
      };
    }

    if (!supplier.support_live_scan) {
      return {
        isValid: false,
        message: `${supplier.name} is not yet supported for Live Scan.
Use the Universal Live Scan or email us at contact@multisellerpro.com to request it - we'll add it ASAP!`
      };
    }

    const normalizedName = supplier.name.toLowerCase().trim();
    const isSupported = SUPPORTED_SUPPLIER_IDS.includes(normalizedName);
    const isUnderMaintenance = UNDERMAINTENANCE_SUPPLIERS.includes(normalizedName);

    if (!isSupported) {
      return {
        isValid: false,
        message: `${supplier.name} is not yet supported for Live Scan.  
Use the Universal Live Scan or email us at contact@multisellerpro.com to request it - we'll add it ASAP!`
      };
    }

    if (isUnderMaintenance) {
      return {
        isValid: false,
        message: `${supplier.name} is temporarily under maintenance. Please try again later or use the Universal Live Scan!`
      };
    }

    return {
      isValid: true,
      supplier
    };

  } catch (e) {
    return {
      isValid: false,
      message: 'Invalid URL format'
    };
  }
};


export function detectPackCount(title) {
  if (!title) return 1;
  const t = title.toLowerCase();
  const patterns = [
    /(\d+)\s*-?\s*pack/i,
    /pack\s*of\s*(\d+)/i,
    /count\s*of\s*(\d+)/i,
    /(\d+)\s*count/i,
    /set\s*of\s*(\d+)/i,
    /(\d+)\s*piece/i,
    /(\d+)\s*pc\b/i,
    /(\d+)\s*pcs\b/i,
    /(\d+)\s*pk\b/i,
    /value\s*pack\s*(\d+)/i,
    /(\d+)\s*x\s*\d/i,
    /\bx(\d+)\b/i,
    /\b(\d+)pack\b/i,
    /bundle\s*of\s*(\d+)/i,
    /(\d+)\s*bottle/i,
    /(\d+)\s*bag/i,
    /(\d+)\s*box/i,
    /(\d+)\s*unit/i,
    /(\d+)\s*roll/i,
    /^(\d+)[xX]-/,
    /^(\d+)[xX]\s/,
  ];
  for (const re of patterns) {
    const m = t.match(re);
    if (m) {
      const n = parseInt(m[1]);
      if (n >= 2) return n;
    }
  }
  return 1;
}